import { useEffect } from 'react';

import {
  ApplicationStatusUI,
  useGetApplicationByIdQuery,
} from '~entities/application';
import { useGenerateDocumentMutation } from '~entities/document';

import { Loader } from '~shared/ui/loader';
import { MoneyPh } from '~shared/ui/money-ph';

import { ButtonLink } from '@platform-ui/button';

import { format } from 'date-fns';

import type { ApplicationsTableData } from '../../model/applications-table-data';

export const ApplicationDetails = ({
  application,
}: {
  application: ApplicationsTableData;
}) => {
  const {
    data: fullApplication,
    isLoading,
    isSuccess,
  } = useGetApplicationByIdQuery(application.applicationId);
  const {
    mutate: generateDocument,
    isPending: isGenerationInProgress,
    data: documentData,
  } = useGenerateDocumentMutation();

  useEffect(() => {
    if (documentData) {
      const file = new Blob([documentData], {
        type: 'application/pdf',
      });

      const fileURL = window.URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    }
  }, [documentData]);
  const dateObj = new Date(application.applicationTimestampUTC);
  const datePart = format(dateObj, 'dd-MM-yyyy');
  const timePart = format(dateObj, 'hh:mm a');

  const handleGenerateDocument = () => {
    generateDocument(application.applicationId);
  };

  const showOrderItemsSection =
    isSuccess && !!fullApplication?.orderItems?.length;
  const showLoader = isLoading || isGenerationInProgress;

  return (
    <div className="flex flex-col gap-4 pt-2 pb-4 text-sm text-base-primary">
      {showLoader && <Loader />}
      <section>
        <h2 className="m-0 mb-3 text-2xl font-bold">
          {application.agreementId}
        </h2>
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          <li className="flex justify-between">
            <span className="text-base">Status</span>
            <span className="text-base">
              <ApplicationStatusUI status={application.applicationStatus} />
            </span>
          </li>
          <li className="flex justify-between">
            <span className="text-base">Date&nbsp;&&nbsp;time</span>
            <span className="text-base">
              {datePart}, {timePart}
            </span>
          </li>
          <li className="flex justify-between">
            <span className="text-base">Sale amount</span>
            <span className="text-base">
              <MoneyPh value={application.saleAmount} />
            </span>
          </li>
          <li className="flex justify-between">
            <span className="text-base">Down payment</span>
            <span className="text-base">
              <MoneyPh value={application.downPaymentAmount} />
            </span>
          </li>
          <li className="flex justify-between">
            <span className="text-base">Financed amount</span>
            <span className="text-base">
              <MoneyPh value={application.financedAmount} />
            </span>
          </li>
        </ul>
      </section>
      <section>
        <h2 className="m-0 mb-3 text-2xl font-bold">Store</h2>
        <span className="text-base">{application.pos.fullAddress}</span>
      </section>

      {showOrderItemsSection && (
        <section>
          <h2 className="m-0 mb-3 text-2xl font-bold">Purchased items</h2>

          <ul className="m-0 flex min-h-[20px] list-none flex-col gap-3 p-0">
            {fullApplication.orderItems?.map((item) => (
              <li key={item.name} className="flex justify-between">
                <span className="text-base">
                  {item.name}&nbsp;{item.isUsed && '(Used)'}
                </span>
                <span className="text-base">x&nbsp;{item.quantity}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-3">
        <ButtonLink
          disableHorizontalPadding
          disableVerticalPadding
          size="m"
          onClick={handleGenerateDocument}
        >
          See loan notification
        </ButtonLink>
      </section>
    </div>
  );
};
